<template>
  <div>
    <!--Content Grid -->
    <Dialog
      v-model="isWindowOpen"
      :show-icon="false"
      :persistent="true"
      max-width="800px"
      min-height="900px"
      :content-class="'physician-picker-dialog'"
      :show-actions="false"
      @cancel="searchInput = ''"
    >
      <template #content>
        <!-- Title -->
        <div class="dialog-title">
          {{ t("Select physician") }}
        </div>
        <!-- Filter -->
        <ToolbarSearchInput
          v-model="searchInput"
          :placeholder="t('Find a physician')"
          class="toolbar-search-input"
        />
        <!-- Physicians List -->
        <v-data-table
          v-model:page="page"
          :headers="headers"
          :items="physicians"
          :items-per-page="itemsPerPage"
          :loading="isLoading"
          :loading-text="t('Loading Content')"
          :no-data-text="t('No data available')"
          hide-default-footer
          @page-count="pageCount = $event"
        >
          <template #item.actions="{ item }">
            <Button
              color="text-primary"
              icon-btn
              icon-with-background
              class="mr-2 btn-fab--small text-primary"
              @click="onSelectPhysician(item)"
            >
              <v-icon
                class="icon-flipped"
                size="20"
              >
                mdi-keyboard-backspace
              </v-icon>
            </Button>
          </template>
        </v-data-table>
        <Pagination
          v-model="page"
          :page-count="pageCount"
        />
      </template>
      >
    </Dialog>

    <!--Show Button-->
    <div class="v-data-table-column--align-start mb-3">
      <Button
        @click="openPickerWindow()"
      >
        {{ t("Select physician") }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted} from 'vue';
import Button from "@/components/input-components/Button.vue";
import Dialog from "@/components/dialogs/Dialog.vue";
import ToolbarSearchInput from "@/components/common/ToolbarSearchInput.vue";
import { Physician } from "@/entities/physician";
import { usePhysiciansStore } from '@/stores/physicians'
import { useI18n } from 'vue-i18n';
import Pagination from "@/components/common/Pagination.vue";

const { t } = useI18n();
const physiciansStore = usePhysiciansStore();

const page = ref(1);
const itemsPerPage = ref(10);
const isLoading = ref(false);
const isWindowOpen = ref(false);
const headers = computed(() => [
  { title: t("First name"), align: "start", sortable: false, value: "firstName", width: "15%" },
  { title: t("Last name"), align: "start", sortable: false, value: "lastName", width: "17%" },
  { title: t("Email"), align: "start", sortable: false, value: "email", width: "36%" },
  { title: t("City"), align: "start", sortable: false, value: "city", width: "20%" },
  { title: t("Actions"), align: "end", sortable: false, value: "actions", width: "12%" },
]);

const pageCount = computed(() => {
  const total = physicians.value?.length ?? 0
  const perPage = itemsPerPage.value || 1
  return Math.max(1, Math.ceil(total / perPage))
})

const physicians = computed(() => physiciansStore.getPhysicians)
const pagedPhysicians = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return physicians.value.slice(start, end)
})

const searchInput = computed({
  get: () => physiciansStore.getSearchInput,
  set: (value: string) => physiciansStore.searchInput = value,
})

function openPickerWindow() {
  isWindowOpen.value = true;
}
function closePickerWindow() {
  isWindowOpen.value = false;
}

async function fetchPhysicians() {
  isLoading.value = true;
  await physiciansStore.fetchPhysicians();
  isLoading.value = false;
}

function onSelectPhysician(physician: Physician) {
  emit('physicianSelected', physician);
  closePickerWindow();
}

const emit = defineEmits(['physicianSelected']);

onMounted(async () => {
  searchInput.value = ''
  await fetchPhysicians();
});
</script>

<style lang="scss">
.physician-picker-dialog {
  .dialog-title {
    text-transform: uppercase;
    font-weight: bold;
    margin-bottom: 30px;
    font-size: 20px;
    letter-spacing: 1px;
    margin-top: -20px;
  }
  .v-card {
    padding: 25px 35px !important;
  }
  .v-card__actions {
    justify-content: center;
  }

  .v-data-table__td {
    word-break: break-word !important;
  }

  @media #{map.get($display-breakpoints, 'xs-only')} {
    .toolbar-search-input {
      flex-direction: column;

      > button {
        margin-top: 0.5rem;
        margin-left: 0 !important;
      }
    }
  }

  .v-dialog {
    position: relative;
    z-index: 502 !important;
  }
}
</style>
